import captionService from "CaptionService";
import { PageType } from "Constants";
import { FormFlowAbortError } from "Errors";
import { type FormTemplate } from "FormTemplateService";
import { loadTemplateAsync } from "ModuleLoader";
import type { TaskPageExtensions } from "TaskPresenter";
import type TaskViewModel from "TaskViewModel";
import { observeTitle } from "TitleService";
import vueFactory from "VueFactory";
import ko, { type BindingContext, type Computed } from "knockout";
import { nextTick, type ComponentOptions } from "vue";

export type DrawerInterface = {
  showComponent: (component: ComponentOptions<Vue>, title: string | Computed<string>, viewModel: TaskViewModel) => void;
  hide: () => void;
};

export class DrawerService {
  private viewModel: TaskViewModel | undefined;
  private drawer: DrawerInterface | undefined;

  processing: Readonly<boolean>;
  constructor() {
    this.viewModel = undefined;
    this.processing = false;
    this.drawer = undefined;
  }

  registerDrawer(drawer: DrawerInterface): void {
    this.drawer = drawer;
  }

  unregisterDrawer(): void {
    this.drawer = undefined;
  }

  /**
   * Show the drawer containing the provided form.
   * Will associate the drawer with the provided ViewModel & Page Extensions.
   * If the service is busy processing a drawer it will reject new requests.
   *
   * @param form Form template or promise that resolves to one for the contents of the drawer.
   * @param viewModel Instance of TaskViewViewModel
   * @param pageExtensions Instance of TaskPageExtensions
   * @returns a promise that resolves to an element attached to the DOM & the binding context of the View.
   */
  async showDrawerAsync(
    form: FormTemplate | Promise<FormTemplate>,
    viewModel: TaskViewModel,
    pageExtensions: TaskPageExtensions,
  ): Promise<[JQuery<Element>, BindingContext]> {
    if (this.processing) {
      throw new FormFlowAbortError();
    }

    this.processing = true;

    const shell = await loadTemplateAsync(PageType.DrawerTask.Shell);

    const bindingContext = new ko.bindingContext(viewModel);
    bindingContext.$contentViewModel = viewModel;
    if (viewModel) {
      viewModel.pageExtensions = pageExtensions;
    }

    const formOptions = await form;

    const $template = $("<div>");
    $(shell).appendTo($template);

    const $content = $template.find("#content").attr("id", formOptions.PK);
    $content.html(formOptions.Markup);

    let title: string | Computed<string> = formOptions.FormID;

    if (formOptions.CaptionArgs) {
      title = observeTitle(viewModel.getEntity(), formOptions.Caption, formOptions.CaptionArgs);
    } else if (formOptions.Caption) {
      title = captionService.getStringFromInfo(formOptions.Caption);
    }

    this.viewModel = viewModel;

    const component = await vueFactory.createVueComponentAsync({
      contentContainer: $template[0],
      validationRegistrar: pageExtensions && pageExtensions.validationRegistrar,
      knockoutContext: bindingContext,
      bindingContext: viewModel,
      name: "GlowDrawerContext",
      formInfo: { id: formOptions.PK, inDrawer: true },
      formExtender: formOptions.ExtenderFunc,
    });

    if (this.drawer) {
      this.drawer.showComponent(component, title, viewModel);
    }
    await nextTick();

    this.processing = false;
    return [$(`#${formOptions.PK}`), bindingContext];
  }

  removeDrawer(): void {
    this.drawer?.hide();
  }

  hasChanges(): boolean {
    if (this.viewModel) {
      return this.viewModel.hasChanges();
    }
    return false;
  }

  ensureSavedAsync(): Promise<boolean> {
    if (this.viewModel) {
      return this.viewModel.saveWithPromptAsync();
    }
    return Promise.resolve(true);
  }
}

export default new DrawerService();
